import { useMutation, useQuery, useQueryClient } from "react-query";
import API from "./utils/api";
import ENDPOINTS from "./utils/endpoints";
import {
  AddProductToMerchantRequestCreateDto,
  OnboardMerchantDto,
  PaymentSetupDto,
  PaymentSetupPaginator,
  QueryDraftPaymentSetupDto,
  QueryMerchantRequestDto,
  QueryPaymentSetupDto,
  SearchMerchantRequestDto,
  UpdateOnboardedMerchantDto,
  UpdatePaymentSetupDto,
  UpdateProductToMerchantRequestCreateDto,
} from "../models/payment-setup.dto";
import { toast } from "react-toastify";

const paymentSetupEndpoints = ENDPOINTS.PAYMENTS.PAYMENT_SETUP;

function invalidatePaymentSetupQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries(
    paymentSetupEndpoints.GET_COMPANY_PAYMENT_SETUPS,
  );
  queryClient.invalidateQueries(
    paymentSetupEndpoints.GET_DRAFT_MERCHANT_REQUESTS,
  );
  queryClient.invalidateQueries(
    paymentSetupEndpoints.GET_MERCHANT_REQUEST_LIST,
  );
  queryClient.invalidateQueries(paymentSetupEndpoints.SEARCH_MERCHANT_REQUEST);
  queryClient.invalidateQueries(paymentSetupEndpoints.GET_MERCHANT_REQUEST);
}

export function useOnboardMerchants() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      companyId,
      payload,
    }: {
      companyId: number;
      payload: OnboardMerchantDto;
    }) => API.PaymentAPI.onboardMerchants(companyId, payload),
    {
      onError: API.Handlers.onErrorHandler,
      onSettled: () => invalidatePaymentSetupQueries(queryClient),
    },
  );
}

export function useAddRequestSections() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload }: { id: number; payload: UpdatePaymentSetupDto }) =>
      API.PaymentAPI.addRequestSections(id, payload),
    {
      onError: API.Handlers.onErrorHandler,
      onSettled: () => invalidatePaymentSetupQueries(queryClient),
    },
  );
}

export function useAddProductRequest() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number;
      payload: AddProductToMerchantRequestCreateDto;
    }) => API.PaymentAPI.addProductRequest(id, payload),
    {
      onError: API.Handlers.onErrorHandler,
      onSettled: () => invalidatePaymentSetupQueries(queryClient),
    },
  );
}

export function useSubmitUpdateOnboardedMerchantRequest() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload }: { id: number; payload: UpdateOnboardedMerchantDto }) =>
      API.PaymentAPI.submitUpdateOnboardedMerchantRequest(id, payload),
    {
      onError: API.Handlers.onErrorHandler,
      onSettled: () => invalidatePaymentSetupQueries(queryClient),
    },
  );
}

export function useUpdateSubmittedMerchantRequest() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload }: { id: number; payload: UpdateOnboardedMerchantDto }) =>
      API.PaymentAPI.updateSubmittedMerchantRequest(id, payload),
    {
      onError: API.Handlers.onErrorHandler,
      onSettled: () => invalidatePaymentSetupQueries(queryClient),
    },
  );
}

export function useUpdateSubmittedProductRequest() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateProductToMerchantRequestCreateDto;
    }) => API.PaymentAPI.updateSubmittedProductRequest(id, payload),
    {
      onError: API.Handlers.onErrorHandler,
      onSettled: () => invalidatePaymentSetupQueries(queryClient),
    },
  );
}

export function useDraftMerchantRequest() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      companyId,
      payload,
    }: {
      companyId: number;
      payload: UpdateOnboardedMerchantDto;
    }) => API.PaymentAPI.draftMerchantRequest(companyId, payload),
    {
      onError: API.Handlers.onErrorHandler,
      onSettled: () => invalidatePaymentSetupQueries(queryClient),
    },
  );
}

export function useUpdateDraftedMerchantRequest() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      id,
      companyId,
      payload,
    }: {
      id: number;
      companyId: number;
      payload: UpdateOnboardedMerchantDto;
    }) => API.PaymentAPI.updateDraftedMerchantRequest(id, companyId, payload),
    {
      onError: API.Handlers.onErrorHandler,
      onSettled: () => invalidatePaymentSetupQueries(queryClient),
    },
  );
}

export function useDeleteDraftedMerchantRequest() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ companyId, id }: { companyId: number; id: number }) =>
      API.PaymentAPI.deleteDraftedMerchantRequest(id, companyId),
    {
      onSuccess: () => {
        toast.success("Record deleted successfully");
      },
      onError: API.Handlers.onErrorHandler,
      onSettled: () => invalidatePaymentSetupQueries(queryClient),
    },
  );
}

export function useGetDraftedMerchantRequestList(
  companyId: number,
  options?: QueryDraftPaymentSetupDto,
) {
  const url = paymentSetupEndpoints.GET_DRAFT_MERCHANT_REQUESTS;
  const request = (options: QueryDraftPaymentSetupDto) =>
    API.PaymentAPI.getDraftMerchantRequests(companyId, options);

  const { data, isLoading, error, refetch } = useQuery<
    PaymentSetupPaginator,
    Error
  >(
    [url, companyId, options],
    ({ queryKey, pageParam }) =>
      request(Object.assign({}, queryKey[2], pageParam)),
    { ...API.Handlers.pageParamOption, enabled: !!companyId },
  );

  return { data, isLoading, error, refetch };
}

export function useFinalizeMerchantRequestDraft() {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      id,
      companyId,
      payload,
    }: {
      id: number;
      companyId: number;
      payload: OnboardMerchantDto;
    }) => API.PaymentAPI.finalizeMerchantRequestDraft(id, companyId, payload),
    {
      onError: API.Handlers.onErrorHandler,
      onSettled: () => invalidatePaymentSetupQueries(queryClient),
    },
  );
}

/** A payroll company's own payment setups (drafts and submitted requests) */
export function useGetCompanyPaymentSetups(
  companyId: number,
  query?: QueryPaymentSetupDto,
) {
  const url = paymentSetupEndpoints.GET_COMPANY_PAYMENT_SETUPS;
  const { data, isLoading, error, refetch } = useQuery<
    PaymentSetupPaginator,
    Error
  >(
    [url, companyId, query],
    () => API.PaymentAPI.getCompanyPaymentSetups(companyId, query),
    { enabled: !!companyId, retry: false },
  );
  return { data, isLoading, error, refetch };
}

export function useGetMerchantRequestList(query?: QueryMerchantRequestDto) {
  const url = paymentSetupEndpoints.GET_MERCHANT_REQUEST_LIST;
  const { data, isLoading, error, refetch } = useQuery<
    PaymentSetupPaginator,
    Error
  >([url, query], () => API.PaymentAPI.getMerchantRequestList(query));
  return { data, isLoading, error, refetch };
}

export function useSearchMerchantRequest(query?: SearchMerchantRequestDto) {
  const url = paymentSetupEndpoints.SEARCH_MERCHANT_REQUEST;
  const { data, isLoading, error, refetch } = useQuery<
    PaymentSetupPaginator,
    Error
  >([url, query], () => API.PaymentAPI.searchMerchantRequest(query));
  return { data, isLoading, error, refetch };
}

export function useGetMerchantRequestIssues(id: number) {
  const url = paymentSetupEndpoints.GET_MERCHANT_REQUEST_ISSUES;
  const { data, isLoading, error, refetch } = useQuery<any, Error>(
    [url, id],
    () => API.PaymentAPI.getMerchantRequestIssues(id),
    { enabled: !!id },
  );
  return { data, isLoading, error, refetch };
}

export function useGetMerchantRequest(
  id: number,
  query?: QueryMerchantRequestDto,
) {
  const url = paymentSetupEndpoints.GET_MERCHANT_REQUEST;
  const { data, isLoading, error, refetch } = useQuery<
    { data: PaymentSetupDto },
    Error
  >([url, id, query], () => API.PaymentAPI.getMerchantRequest(id, query), {
    enabled: !!id,
  });
  return { data, isLoading, error, refetch };
}

export function useValidatePaymentAccount() {
  return useMutation(
    ({ payload }: { payload: any }) =>
      API.PaymentAPI.validatePaymentAccount(payload),
    { onError: API.Handlers.onErrorHandler },
  );
}

export function useGetPaymentAccountName() {
  return useMutation(
    ({ payload }: { payload: any }) =>
      API.PaymentAPI.getPaymentAccountName(payload),
    { onError: API.Handlers.onErrorHandler },
  );
}
